import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import { repoApi } from '../../api/client';
import { DiffView } from '../../components/DiffView';
import './CommitDetail.css';

interface CommitData {
    sha: string;
    tree: string;
    parents: string[];
    message: string;
    author: { name: string; email: string; date: string };
    committer: { name: string; email: string; date: string };
}

interface DiffFile {
    path: string;
    status: 'added' | 'deleted' | 'modified';
    oldSha?: string;
    newSha?: string;
    additions: number;
    deletions: number;
}

interface DiffData {
    sha: string;
    parent: string | null;
    files: DiffFile[];
    stats: {
        additions: number;
        deletions: number;
        changed: number;
    };
}

interface CommitDetailPageProps {
    repoPath?: string;
    sha?: string;
}

export function CommitDetailPage({ repoPath: propRepoPath, sha: propSha }: CommitDetailPageProps = {}) {
    const { owner, repo, sha: paramSha } = useParams();
    const repoPath = propRepoPath ?? (owner && repo ? `${owner}/${repo}` : '');
    const sha = propSha ?? paramSha ?? '';

    const [commit, setCommit] = useState<CommitData | null>(null);
    const [diff, setDiff] = useState<DiffData | null>(null);
    const [loading, setLoading] = useState(true);
    const [diffLoading, setDiffLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (repoPath && sha) {
            loadCommit();
        } else {
            setLoading(false);
            setError(repoPath && !sha ? '缺少提交 SHA' : '缺少仓库路径');
        }
    }, [repoPath, sha]);

    const loadCommit = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await repoApi.getCommit(repoPath, sha);
            setCommit(data);
            // 加载 diff
            loadDiff();
        } catch (err: any) {
            setError(err.message || '加载提交详情失败');
        } finally {
            setLoading(false);
        }
    };

    const loadDiff = async () => {
        try {
            setDiffLoading(true);
            const data = await repoApi.getDiff(repoPath, sha);
            setDiff(data);
        } catch (err: any) {
            console.error('加载 diff 失败:', err);
        } finally {
            setDiffLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getMessageTitle = (message: string) => message.split('\n')[0];
    const getMessageBody = (message: string) => message.split('\n').slice(1).join('\n').trim();

    if (loading) {
        return (
            <div className="commit-detail-page">
                <div className="loading">加载中...</div>
            </div>
        );
    }

    if (error || !commit) {
        return (
            <div className="commit-detail-page">
                <div className="error-state">
                    <h2>加载失败</h2>
                    <p>{error || '提交不存在'}</p>
                    <Link to={`/${repoPath}`} className="btn btn-primary">返回仓库</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="commit-detail-page">
            <div className="page-header">
                <Link to={`/${repoPath}/-/commits/main`} className="back-link">
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path fill="currentColor" d="M7.78 12.53a.75.75 0 0 1-1.06 0L2.47 8.28a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 1.06L4.81 7h8.44a.75.75 0 0 1 0 1.5H4.81l2.97 2.97a.75.75 0 0 1 0 1.06Z" />
                    </svg>
                    返回提交历史
                </Link>
            </div>

            <div className="commit-card">
                <div className="commit-header">
                    <h1 className="commit-title">{getMessageTitle(commit.message)}</h1>
                    {getMessageBody(commit.message) && (
                        <pre className="commit-body">{getMessageBody(commit.message)}</pre>
                    )}
                </div>

                <div className="commit-meta">
                    <div className="meta-row">
                        <div className="author-info">
                            <div className="avatar">{commit.author.name.charAt(0).toUpperCase()}</div>
                            <div className="info">
                                <span className="name">{commit.author.name}</span>
                                <span className="email">&lt;{commit.author.email}&gt;</span>
                                <span className="time">提交于 {formatDate(commit.author.date)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="meta-details">
                        <div className="detail-item">
                            <span className="label">提交 SHA</span>
                            <code className="value">{commit.sha}</code>
                            <button
                                className="copy-btn"
                                onClick={() => navigator.clipboard.writeText(commit.sha)}
                                title="复制"
                            >
                                <svg viewBox="0 0 16 16" width="14" height="14">
                                    <path fill="currentColor" d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25Z" />
                                </svg>
                            </button>
                        </div>
                        <div className="detail-item">
                            <span className="label">Tree SHA</span>
                            <code className="value">{commit.tree.substring(0, 7)}</code>
                        </div>
                        {commit.parents.length > 0 && (
                            <div className="detail-item">
                                <span className="label">父提交</span>
                                <div className="parents">
                                    {commit.parents.map((parent) => (
                                        <Link key={parent} to={`/${repoPath}/-/commit/${parent}`} className="parent-link">
                                            {parent.substring(0, 7)}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="actions-bar">
                <Link to={`/${repoPath}/-/tree/${commit.sha}`} className="btn btn-secondary">
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path fill="currentColor" d="M2 1.75C2 .784 2.784 0 3.75 0h6.586c.464 0 .909.184 1.237.513l2.914 2.914c.329.328.513.773.513 1.237v9.586A1.75 1.75 0 0 1 13.25 16h-9.5A1.75 1.75 0 0 1 2 14.25Z" />
                    </svg>
                    浏览此提交的文件
                </Link>
            </div>

            <div className="diff-section">
                <div className="section-header">
                    <h2>文件变更</h2>
                </div>
                {diffLoading ? (
                    <div className="diff-loading">加载变更中...</div>
                ) : diff ? (
                    <DiffView files={diff.files} stats={diff.stats} />
                ) : (
                    <div className="diff-placeholder">
                        <p>无法加载文件变更</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CommitDetailPage;

